import { useDeleteCallStatus, useCreateCallStatus } from "hooks/useCallStatus";
import useUserId from "hooks/useUserId";
import React, { useContext, useEffect, useRef, useState } from "react";
import io from 'socket.io-client';
import { VideoCallContext } from "store/VideoCallContext";
import { CallersIdContext } from "store/callersIdContext";

const apiUrlMiror = process.env.REACT_APP_API_MIROR_URL;

function VideoCallView({ 
  meetingLink, 
  setSelectedContactId, 
  choosenContacts, 
  end_Call, 
  setCallData, 
  callData, 
  setFromOtherPage, 
  setCallModalOpen,
  userId: userIdProp, // NEW: Accept userId as prop
  senderId: senderIdProp // NEW: Accept senderId as prop (alternative name)
}) {
  const { userId: userIdFromHook } = useUserId();
  const socket = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notification, setNotification] = useState('');
  const [roomUsers, setRoomUsers] = useState({});
  
  const { create } = useCreateCallStatus();
  const { remove } = useDeleteCallStatus();
  const { videoCallLaunched, setVideoCallLaunched } = useContext(VideoCallContext);
  const { callers, setCallers } = useContext(CallersIdContext);

  // Use prop if provided, otherwise fall back to hook
  const userId = userIdProp || senderIdProp || userIdFromHook;
  const stringUserId = userId ? String(userId) : null;
  // Use the same roomId format as MiroTalk (passed via URL params, which stores it as-is)
  // The meetingLink is passed to MiroTalk iframe as: roomId=${encodeURIComponent(meetingLink)}
  // But MiroTalk stores the decoded value from URL params, so we use the raw meetingLink
  const stringRoomId = String(meetingLink);
  
  // Log userId for debugging
  useEffect(() => {
    console.log('VideoCallView userId:', { 
      userId, 
      stringUserId, 
      userIdProp, 
      senderIdProp, 
      userIdFromHook 
    });
  }, [userId, stringUserId, userIdProp, senderIdProp, userIdFromHook]);

  const handleRoomEmpty = ({ roomId }) => {
    console.log(`Received roomEmpty event for room ${roomId}`);
    
    // Reset the video call state
    setVideoCallLaunched(false);
    setSelectedContactId(null);
    deleteInCall();
    setFromOtherPage(false);
    setCallModalOpen(false);
    
    // Reset call data to ensure clean state for next call
    if (setCallData) {
      console.log('🔄 Resetting call data after room empty');
      setCallData(null);
    }

    const strLeftRoomId = String(roomId);

setCallers(prev => {
  console.log('Previous callers:', prev);

  const updatedCallers = prev.filter(caller => {
    const roomIdStr = String(caller.roomId);
    const shouldKeep = roomIdStr !== strLeftRoomId;
    console.log(`Checking roomId: ${roomIdStr}. strLeftRoomId: ${strLeftRoomId}. Keep this caller? ${shouldKeep}`);
    return shouldKeep;
  });

  console.log('Updated callers:', updatedCallers);
  return updatedCallers;
});

    // Optionally, show a notification to the user
    setNotification('The room is now empty.');
  };

  const leaveRoom = () => {
    console.log('🚪 leaveRoom called - initiating leave sequence');
    
    // Safety check: Ensure we have a valid userId
    if (!stringUserId) {
      console.error('❌ Cannot leave room: stringUserId is null or undefined');
      console.error('userId values:', { 
        stringUserId, 
        userId: userIdProp || senderIdProp || userIdFromHook 
      });
      
      // Still try to reset UI even if we can't emit the socket event
      setVideoCallLaunched(false);
      setSelectedContactId(null);
      setFromOtherPage(false);
      setCallModalOpen(false);
      return;
    }
    
    console.log('Socket connection status:', {
      exists: !!socket.current,
      connected: socket.current?.connected,
      readyState: socket.current?.connected ? 'connected' : 'disconnected'
    });
    
    // IMMEDIATELY reset UI state when user initiates leaving
    // Don't wait for server confirmation
    console.log('🔄 Immediately resetting UI state...');
    
    try {
      // Delete in-call status from backend
      console.log('Deleting in-call status...');
      deleteInCall();
      
      // Reset all UI state IMMEDIATELY
      console.log('Setting videoCallLaunched to false...');
      setVideoCallLaunched(false);
      setSelectedContactId(null);
      setFromOtherPage(false);
      setCallModalOpen(false);
      
      // Reset call data to ensure clean state for next call
      if (setCallData) {
        console.log('🔄 Resetting call data on leave room');
        setCallData(null);
      }
      
      console.log('✅ UI state reset completed');
    } catch (error) {
      console.error('❌ Error during UI reset:', error);
    }
    
    // Then emit the socket event to notify server and other users
    if (socket.current?.connected) {
      console.log('✅ Emitting userLeft event to server...', {
        userId: stringUserId,
        roomId: stringRoomId
      });
      
      socket.current.emit('userLeft', {
        userId: stringUserId,
        roomId: stringRoomId
      });
      
      console.log('✅ userLeft event emitted successfully');
    } else {
      console.warn('⚠️ Cannot emit userLeft: Socket not connected');
    }
    
    // Clean up socket after leaving so next call can establish fresh connection
    if (socket.current) {
      console.log('🔌 Cleaning up socket after leaving room...');
      socket.current.removeAllListeners();
      socket.current.disconnect();
      socket.current = null;
      console.log('✅ Socket cleaned up - ready for next call');
    }
  };

  const handleUserLeft = ({ userId: leftUserId, roomId }) => {
    console.log(`📢 Received userLeft event: User ${leftUserId} left room ${roomId}`);
      
    const strLeftUserId = String(leftUserId);
    const strLeftRoomId = String(roomId);
    
    console.log('Comparing IDs:', {
      strLeftUserId,
      stringUserId,
      strLeftRoomId,
      stringRoomId,
      userIdMatch: strLeftUserId === stringUserId,
      roomIdMatch: strLeftRoomId === stringRoomId
    });
    
    // IMPORTANT: Only process if BOTH userId AND roomId match the current session
    // This prevents old userLeft events from affecting new calls
    if (strLeftUserId === stringUserId && strLeftRoomId === stringRoomId) {
      console.log('✅ Current user left current room - confirmed by server');
      leaveRoom();
    } else if (strLeftUserId === stringUserId && strLeftRoomId !== stringRoomId) {
      console.log('⚠️ Ignoring userLeft event for OLD room:', strLeftRoomId);
      console.log('   Current room is:', stringRoomId);
      // Don't call leaveRoom() - this is an old event for a previous call
    } else {
      console.log(`👋 Other user ${leftUserId} left the room`);
      // Handle other users leaving if needed
    }
  };

  useEffect(() => {
    console.log('New callers:', callers);
  }, [callers]);

  const connectSocket = () => {
    if (socket.current?.connected) {
      console.log('Socket already connected');
      return;
    }
    
    // Clean up existing socket if present
    if (socket.current) {
      console.log('🧹 Cleaning up existing socket before reconnect');
      socket.current.removeAllListeners();
      socket.current.disconnect();
      socket.current = null;
    }
    
    // Safety check: Don't connect if userId is null
    if (!stringUserId) {
      console.error('❌ Cannot connect socket: stringUserId is null or undefined');
      console.error('Will retry when userId becomes available');
      return;
    }

    console.log('🔌 Connecting to socket with userId:', stringUserId);
    
    // socket.current = io('http://localhost:3001', {
    socket.current = io(apiUrlMiror, {
      withCredentials: true,
      transports: ['websocket'],
      reconnectionAttempts: 3,
      reconnectionDelay: 1000,
      reconnection: true 
    });

    // Connection events
    socket.current.on('connect', () => {
      console.log('✅ Socket connected - joining room...');
      console.log('📍 React socket ID:', socket.current.id);
      setIsConnected(true);
      
      if (stringUserId) {
        console.log('🔔 Emitting joinRoom with:', { 
          userId: stringUserId, 
          roomId: stringRoomId,
          roomIdLength: stringRoomId?.length 
        });
        socket.current.emit('joinRoom', { userId: stringUserId, roomId: stringRoomId });
      } else {
        console.error('❌ Cannot join room: stringUserId is null');
      }
    });

    socket.current.on('disconnect', (reason) => {
      console.log('Socket disconnected. Reason:', reason);
      setIsConnected(false);
      setNotification('Connection lost. Attempting to reconnect...');
    });

    // Room events
    socket.current.on('userJoined', ({ userId: joinedUserId, roomId }) => {
      console.log(`User ${joinedUserId} joined room ${roomId}`);
      AddInCall();
    });

    socket.current.on('userLeft', handleUserLeft);
    socket.current.on('roomEmpty', handleRoomEmpty);

    // Error handling
    socket.current.on('error', (error) => {
      console.error('Socket error:', error);
      setNotification('Connection error occurred.');
    });

    socket.current.on('connect_error', (error) => {
      console.error('Connection error:', error);
      setNotification('Failed to connect. Please check your connection.');
    });

    // DEBUG: Log ALL incoming socket events to see what's being received
    socket.current.onAny((eventName, ...args) => {
      console.log(`🔵 [Socket Event Received] ${eventName}:`, args);
    });
  };
  useEffect(() => {
    if (Object.keys(roomUsers).length === 0 && roomUsers.constructor === Object) {
      console.log('Room is empty');
    }
  }, [roomUsers]);

  
  useEffect(() => {
    // Only attempt to connect if we have a valid userId
    if (stringUserId) {
      connectSocket();
    } else {
      console.warn('⚠️ Waiting for userId to become available before connecting socket');
    }

    return () => {
      console.log('🧹 VideoCallView cleanup starting...');
      
      if (socket.current && socket.current.connected) {
        // Emit the leave room event
        console.log('✅ Emitting userLeft for cleanup...');
        socket.current.emit('userLeft', {
          userId: stringUserId,
          roomId: stringRoomId
        });
        
        // Disconnect socket immediately after emit
        console.log('🔌 Disconnecting socket...');
        socket.current.removeAllListeners(); // Clean up all event listeners
        socket.current.disconnect();
        socket.current = null;
        console.log('✅ Socket disconnected and cleared');
      }
      
      // Ensure state is reset on component unmount
      console.log('🔄 Resetting state on unmount');
      setVideoCallLaunched(false);
      setFromOtherPage(false);
      setCallModalOpen(false);
    };
  }, [meetingLink, stringUserId]); // Added stringUserId dependency

  const AddInCall = async () => {
    try {
      const response = await create({
        user_id: stringUserId,
        in_call: true,
        room_id: stringRoomId,
        receivers: choosenContacts,
      });
      console.log('Call status created:', response);
    } catch (err) {
      console.error('Error creating call status:', err);
    }
  };

  const deleteInCall = async () => {
    try {
      const response = await remove(stringUserId);
      console.log('Call status deleted:', response);
    } catch (err) {
      console.error('Error deleting call status:', err);
    }
  };

  return (
    <div className="video-call-container">
      {notification && (
        <div className="notification-banner">
          {notification}
        </div>
      )}
      {Object.entries(roomUsers).map(([roomId, users]) => (
        <div key={roomId} className="room-info">
          <h3>Room {roomId}</h3>
          <div>Active Users: {users.length}</div>
          <ul>
            {users.map(userId => (
              <li key={userId}>{userId}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default VideoCallView;
// import { useDeleteCallStatus, useCreateCallStatus } from "hooks/useCallStatus";
// import useUserId from "hooks/useUserId";
// import React, { useContext, useEffect, useState } from "react";
// import { SocketContext } from "services/SocketContext";
// import { VideoCallContext } from "store/VideoCallContext";
// import { CallersIdContext } from "store/callersIdContext";

// function VideoCallView({ meetingLink, setSelectedContactId, choosenContacts }) {
//   const { userId } = useUserId();
//   const [notification, setNotification] = useState('');
//   const [roomUsers, setRoomUsers] = useState({});
//   const { create } = useCreateCallStatus();
//   const { remove } = useDeleteCallStatus();
//   const { setVideoCallLaunched } = useContext(VideoCallContext);
//   const { setCallers } = useContext(CallersIdContext);
//   const { socket, isConnected } = useContext(SocketContext);

//   const stringUserId = String(userId);
//   const stringRoomId = String(meetingLink);

//   const handleRoomEmpty = ({ roomId }) => {
//     console.log(`Received roomEmpty event for room ${roomId}`);
//     // Reset the video call state
//     setVideoCallLaunched(false);
//     setSelectedContactId(null);
//     deleteInCall();
//     setCallers(prev => prev.filter(caller => String(caller.roomId) !== String(roomId)));
//     setNotification('The room is now empty.');
//   };

//   const leaveRoom = () => {
//     if (socket && socket.connected) {
//       console.log('Emitting userLeft event...');
//       socket.emit('userLeft', { userId: stringUserId, roomId: stringRoomId });
//       // Delay disconnection to catch roomEmpty event
//       setTimeout(() => {
//         // Optionally disconnect or do any cleanup here
//         console.log('Leaving room after delay.');
//       }, 500); // delay in milliseconds
//     }
//   };
  

//   const handleUserLeft = ({ userId: leftUserId, roomId }) => {
//     console.log(`User ${leftUserId} left room ${roomId}`);
//     if (String(leftUserId) === stringUserId) {
//       console.log('Current user left - cleaning up...');
//       setVideoCallLaunched(false);
//       setSelectedContactId(null);
//       // deleteInCall();
//     }
//   };

//   // Debug: Log all incoming events (temporarily)
//   useEffect(() => {
//     if (!socket) return;
//     const onAny = (event, ...args) => {
//       console.log("Received event:", event, args);
//     };
//     socket.onAny(onAny);
//     return () => {
//       socket.offAny(onAny);
//     };
//   }, [socket]);

//   // Listen for "roomEmpty" events
//   useEffect(() => {
//     if (!socket) return;
//     socket.on("roomEmpty", handleRoomEmpty);
//     return () => {
//       socket.off("roomEmpty", handleRoomEmpty);
//     };
//   }, [socket]);

//   // Listen for "userLeft" events
//   useEffect(() => {
//     if (!socket) return;
//     socket.on("userLeft", handleUserLeft);
//     return () => {
//       socket.off("userLeft", handleUserLeft);
//     };
//   }, [socket]);

//   // Emit joinRoom when connected
//   useEffect(() => {
//     if (socket && isConnected) {
//       socket.emit('joinRoom', { userId: stringUserId, roomId: stringRoomId });
//     }
//   }, [socket, isConnected, stringUserId, stringRoomId]);

//   // Cleanup: leave room on unmount
//   useEffect(() => {
//     return () => {
//       if (socket) leaveRoom();
//     };
//   }, [socket]);

//   const AddInCall = async () => {
//     try {
//       const response = await create({
//         user_id: stringUserId,
//         in_call: true,
//         room_id: stringRoomId,
//         receivers: choosenContacts,
//       });
//       console.log('Call status created:', response);
//     } catch (err) {
//       console.error('Error creating call status:', err);
//     }
//   };

//   const deleteInCall = async () => {
//     try {
//       const response = await remove(stringUserId);
//       console.log('Call status deleted:', response);
//     } catch (err) {
//       console.error('Error deleting call status:', err);
//     }
//   };

//   return (
//     <div className="video-call-container">
//       {notification && <div className="notification-banner">{notification}</div>}
//       {Object.entries(roomUsers).map(([roomId, users]) => (
//         <div key={roomId} className="room-info">
//           <h3>Room {roomId}</h3>
//           <div>Active Users: {users.length}</div>
//           <ul>
//             {users.map(userId => (<li key={userId}>{userId}</li>))}
//           </ul>
//         </div>
//       ))}
//     </div>
//   );
// }

// export default VideoCallView;